import { endOfWeek, formatISO, startOfWeek, subDays, subWeeks } from 'date-fns';
import { FilterValidator } from '../table/models/filter-validator';
import {
  DateFilterAbsoluteValue,
  DateFilterFormGroup,
  DateFilterFormValue,
  DateFilterPeriodValue,
  DateFilterRelativeValue,
} from '../table/models/filter';

export interface PeriodSelectorFormGroup extends DateFilterFormGroup {}

export interface PeriodSelectorFormValue extends DateFilterFormValue {}

/**
 * The period a response was actually built with, as the backend reports it in
 * the `periodSelector` member of `RemoteTable` and `TileGrid` — one shape for
 * IPT (`period` / `statPeriod`) and WMM (`lastWeeks`).
 *
 * It exists because *absence of the period param is the request for the
 * backend's per-page/per-site default*: with nothing sent, only the response
 * can say what was applied, and the picker renders that instead of a value the
 * frontend invented. See `docs/plans/period-defaults/2026-09-09-plan.md`.
 *
 * `mode: 'unfiltered'` (paired with `value: null`) means "no period at all" —
 * what a selector's toggle sends when switched off: the base chip's, or a stat
 * chip's that opts in with `allowInfinitePeriod`.
 */
export interface PeriodSelectorState {
  mode: PeriodSelectorFormValue['mode'] | 'unfiltered';
  /**
   * Derived from the pickers' value union so a new date-filter mode reaches
   * this contract too. `null` — or the member left out, which is the same
   * deviation the backend has been measured making on `enabled` — pairs with
   * `mode: 'unfiltered'`.
   *
   * Deliberately *not* discriminated against `mode`: this is a wire shape, and
   * the helpers below have to be able to take the half-states a backend can
   * actually send. Consumers narrowing on `mode` cast; that cast is honest
   * about what was parsed, not a hole in the model.
   */
  value?: PeriodSelectorFormValue['value'] | null;
  /**
   * Whether the reported period is applied. Canonically `enabled`, **not** the
   * table filters' `isEnabled`. The frontend always serializes `true`; a
   * request that carried no `enabled` field is echoed back with `null`
   * (measured against `ipt.test` on 2026-09-09), so `null` reads as `true`.
   * Optional because only one endpoint has been measured — nothing promises
   * every backend serializes the member, and both helpers tolerate absence.
   */
  enabled?: boolean | null;
}

/**
 * Whether the state stands for "no period filtering at all" — i.e. the rows
 * really are all the data, so a consumer may say so.
 *
 * Either half of the wire marker is accepted: the contract pairs
 * `mode: 'unfiltered'` with `value: null` (or the member left out), and a state
 * carrying only one of the two means the same thing. A missing state is *not*
 * unfiltered — it is a backend that has not migrated yet, or a response type
 * that carries no period. Neither is `enabled: false`: that is a period the
 * backend reports but did not apply, which says nothing about what filtered
 * the rows instead.
 */
export const isUnfiltered = (
  state: PeriodSelectorState | null | undefined
): boolean =>
  !!state &&
  state.enabled !== false &&
  (state.mode === 'unfiltered' || state.value == null);

/**
 * Whether the backend applied a period to build these rows — the *fact*, with
 * no question of whether any picker can draw it.
 *
 * That separation is the point: {@link appliedPeriodFormValue} answers "is
 * there something to render here", and returns `null` for a period a given
 * surface cannot draw; this answers "are the rows narrowed", which stays true
 * either way. Consumers that describe the data rather than display it — the
 * empty state's "no results match the current filters" — read this one.
 */
export const isAppliedPeriod = (
  state: PeriodSelectorState | null | undefined
): boolean => !!state && state.enabled !== false && !isUnfiltered(state);

/**
 * The applied period in the shape the pickers bind to — `null` whenever
 * nothing is applied, which is what renders their off state ("All time").
 *
 * `null` covers three cases: no state at all, the unfiltered state
 * ({@link isAppliedPeriod} owns that test, so they cannot drift), and an
 * `enabled: false` echo — a period the backend reports but does not apply.
 * `enabled: null`/absent reads as `true`.
 *
 * **Deliberately not exported**: the shape translation alone is never what a
 * display path wants — {@link appliedPeriodFormValue} is, and the whole reason
 * it exists is that a wire value has to be checked against the surface about
 * to render it. A second exported mapping next to it is an invitation to copy
 * the wrong one and put an unchecked wire value into a picker.
 */
const periodStateToFormValue = (
  state: PeriodSelectorState | null | undefined
): PeriodSelectorFormValue | null => {
  if (!isAppliedPeriod(state)) {
    return null;
  }

  // `isAppliedPeriod` has ruled out both halves of the unfiltered marker — a
  // real mode and a present value — which the compiler cannot see through a
  // predicate call.
  return { mode: state!.mode, value: state!.value } as PeriodSelectorFormValue;
};

/**
 * Which picker family is about to render the applied period.
 *
 * `'full'` is the base chip and the filter row's date cells: they carry the
 * complete method lists, so `FilterValidator`'s own check is the whole answer.
 * `'stat'` is `PeriodSelectorStatComponent`, whose panel and display branches
 * narrow the absolute and period methods to {@link STAT_ABSOLUTE_METHODS} /
 * {@link STAT_PERIOD_METHODS} — a wire-legal `not_between`, `before` or
 * `not_at` renders an **empty label** there next to a live toggle, and Apply
 * would submit a method that surface never offers.
 */
export type PeriodSelectorSurface = 'full' | 'stat';

/**
 * Whether the surface about to render this value carries an entry for its
 * method. Units are already the column type's business ({@link FilterValidator}
 * narrows those by `date`/`datetime`, which is exactly the picker's own
 * `units` list); methods are the surface's.
 */
const isRenderableMethod = (
  value: PeriodSelectorFormValue,
  surface: PeriodSelectorSurface
): boolean => {
  if (surface !== 'stat') {
    return true;
  }

  const methods =
    value.mode === 'absolute'
      ? STAT_ABSOLUTE_METHODS
      : value.mode === 'period'
        ? STAT_PERIOD_METHODS
        : // The stat chip inherits the base selector's relative methods.
          RELATIVE_METHODS;

  return methods.some((method) => method.value === value.value.method);
};

/**
 * The applied period as a picker/filter value, **validated** against the
 * surface it is about to be rendered on — the only mapping the display paths
 * may use.
 *
 * The applied period is the one value that reaches a live CVA straight off the
 * wire: everything else entering a filter cell or a picker is form-built (a
 * user pick) or `FilterValidator`-checked (the URL restore in
 * `loadFiltersFromRoute`, filter templates in `loadFilterTemplate`). A state
 * whose `value` is malformed for `type` (a relative value with no `unit` or a
 * unit the type does not allow, a `before_within` with no `within`, an
 * unparseable date, a `time` on a `date` column) patches the pickers' forms as
 * *invalid*, which their `writeValue` answers with a silent reset to the off
 * state. So this gate is what keeps the display **truthful**, not what keeps
 * it from looping: without it a malformed echo would read as "no filter" over
 * filtered rows with nothing said about why. `null` here means "nothing
 * displayable was reported", which every consumer already renders as the off
 * state — the difference being that here it is a deliberate answer rather than
 * a form patch that happened to fail.
 *
 * `type` narrows the legal **units** and is the renderer's own filter type —
 * the synced column's for a base chip or a filter cell, `datetime` for the
 * stat chips (which is also their `filter` input's default). `surface` narrows
 * the legal **methods**; see {@link PeriodSelectorSurface}. Pass what will
 * *render* the value, not what the table happens to be filtered on: the two
 * differ on every page that binds a bare stat chip over a date-typed synced
 * column.
 *
 * Whether the rows are period-filtered at all is a different question, and
 * survives a value no surface can draw — {@link isAppliedPeriod}.
 */
export const appliedPeriodFormValue = (
  state: PeriodSelectorState | null | undefined,
  type: 'date' | 'datetime' = 'datetime',
  surface: PeriodSelectorSurface = 'full'
): PeriodSelectorFormValue | null => {
  const value = periodStateToFormValue(state);

  return value &&
    FilterValidator.isValidDateFilterValue(value, type) &&
    isRenderableMethod(value, surface)
    ? value
    : null;
};

/**
 * The payload of the `period` param — HTTP param and URL query param alike.
 *
 * Every sender goes through here, so `enabled: true` cannot be forgotten at
 * one of the send sites — the table and tile-grid requests and the URL writes
 * alike. (The report pages keep their own hand-rolled `JSON.stringify` on
 * purpose: their periods stay explicit, D6.) Pass `'unfiltered'` for the base
 * selector's off state; to ask for the backend's default, send nothing at
 * all — omit the param, don't call this.
 */
export const serializePeriodParam = (
  value: PeriodSelectorFormValue | 'unfiltered'
): string =>
  JSON.stringify(
    value === 'unfiltered'
      ? ({
          mode: 'unfiltered',
          value: null,
          enabled: true,
        } satisfies PeriodSelectorState)
      : { ...value, enabled: true }
  );

/**
 * The name of the table period's query param — `period` in the URL and on the
 * wire alike. (`statPeriod` exists only as a URL name: its tile-grid requests
 * send `period` too.)
 */
export const DEFAULT_PERIOD_SELECTOR_QUERY_PARAM_KEY = 'period';

/**
 * An explicit period choice, as the URL carries it: a picked value, or
 * `'unfiltered'` — a chip switched off, "no period at all". No choice
 * (`null` wherever this type appears) is the request for the backend's
 * default.
 */
export type ExplicitPeriod = PeriodSelectorFormValue | 'unfiltered';

/**
 * Reads a `period` / `statPeriod` URL param back into an explicit choice —
 * `null` when it carries none worth honouring.
 *
 * Takes the raw query-param string or the object the routed inputs have
 * already parsed (`queryParamTransform`), and accepts:
 *
 * - `mode: 'unfiltered'` → `'unfiltered'`. Only the explicit marker: unlike
 *   {@link isUnfiltered}, which reads a response and so tolerates either half
 *   of it, a URL value with a real mode and no `value` is a broken link, not a
 *   request for all data;
 * - a period valid for `type` → that period **without** its `enabled` member.
 *   The serializer writes `enabled: true` into the URL, and a parsed object
 *   still carrying it would travel on into a picker and from there into a
 *   column filter's wire format, which has no such member;
 * - anything else — unparseable JSON, a malformed value, `enabled: false` —
 *   → `null`. A broken deep link then means the same thing to the chip, the
 *   request and the chart: no choice, the backend's default.
 */
export const parsePeriodParam = (
  raw: unknown,
  type: 'date' | 'datetime' = 'datetime'
): ExplicitPeriod | null => {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const state = parsed as PeriodSelectorState;
  if (state.enabled === false) {
    return null;
  }
  if (state.mode === 'unfiltered') {
    return 'unfiltered';
  }
  if (state.value == null || typeof state.value !== 'object') {
    return null;
  }

  const value = {
    mode: state.mode,
    value: state.value,
  } as PeriodSelectorFormValue;
  return FilterValidator.isValidDateFilterValue(value, type) ? value : null;
};

/**
 * The picker value inside an explicit choice — `null` for the off state, which
 * no picker holds as a *value*: `'unfiltered'` is what their toggle renders,
 * not something their form can contain.
 *
 * The one place the distinction is spelled, so a consumer that only wants "the
 * period the user picked" cannot accidentally let `'unfiltered'` through into a
 * form patch, a filter entry or a date computation.
 */
export const explicitPeriodValue = (
  period: ExplicitPeriod | null
): PeriodSelectorFormValue | null => (period === 'unfiltered' ? null : period);

/**
 * What a chip renders: the user's explicit choice when there is one, else the
 * period the response **reports** as applied — `'unfiltered'` where either of
 * them says "no period at all", which is the chips' off state and not a value
 * their form can hold ({@link explicitPeriodValue} is the mapping that strips
 * it, for the consumers that want the picked period alone).
 *
 * An explicit `'unfiltered'` renders the off state at once rather than falling
 * back to `reported`, which still holds the previous response's period until
 * the unfiltered one lands — falling back would flash it back on (the S2
 * toggle-off bounce, D8). That is the whole reason this is not a plain `??`
 * over the two.
 *
 * `reportedState` is the wire state `reported` was mapped from, read only once
 * both halves have come back empty. It is the one thing that tells an
 * unfiltered report — "these rows really are all the data", which a chip may
 * say — from the two cases {@link appliedPeriodFormValue} flattens into the
 * same `null`: no report at all, and a reported period the surface cannot
 * draw. Both of those are the empty state, which means "the backend's default
 * applies". No measured backend reports unfiltered, on either environment; a
 * chip handed one still has to name it rather than claim a default it did not
 * get.
 */
export const appliedChipPeriod = (
  explicit: ExplicitPeriod | null,
  reported: PeriodSelectorFormValue | null,
  reportedState?: PeriodSelectorState | null
): ExplicitPeriod | null =>
  explicit ?? reported ?? (isUnfiltered(reportedState) ? 'unfiltered' : null);

/**
 * The `period` query param of a request that carries an explicit choice —
 * `{}` for no choice, which is the request for the backend's default.
 *
 * The one rule, shared by the table requests ({@link
 * DEFAULT_PERIOD_SELECTOR_QUERY_PARAM_KEY} bound through
 * `BaseListComponent.periodHttpParams`) and the tile-grid service methods, so
 * the param name is spelled once and `serializePeriodParam`'s `enabled: true`
 * cannot be forgotten at one of them.
 */
export const periodParams = (
  period: ExplicitPeriod | null | undefined
): Record<string, string> =>
  period
    ? {
        [DEFAULT_PERIOD_SELECTOR_QUERY_PARAM_KEY]: serializePeriodParam(period),
      }
    : {};

export type QuickPeriodValue =
  | { mode: 'absolute'; value: DateFilterAbsoluteValue }
  | { mode: 'period'; value: DateFilterPeriodValue }
  | { mode: 'relative'; value: DateFilterRelativeValue };

export interface QuickPeriod {
  name: string;
  getValue: () => QuickPeriodValue;
}

const toIsoDate = (date: Date): string =>
  formatISO(date, { representation: 'date' });

export const QUICK_PERIODS: QuickPeriod[] = [
  {
    name: $localize`:@@period.selector.yesterday:Yesterday`,
    getValue: () => ({
      mode: 'absolute',
      value: { method: 'at', value: toIsoDate(subDays(new Date(), 1)) },
    }),
  },
  {
    name: $localize`:@@period.selector.last-week:Last week`,
    getValue: () => {
      const lastWeek = subWeeks(new Date(), 1);
      return {
        mode: 'period',
        value: {
          method: 'between',
          value1: toIsoDate(startOfWeek(lastWeek, { weekStartsOn: 1 })),
          value2: toIsoDate(endOfWeek(lastWeek, { weekStartsOn: 1 })),
        },
      };
    },
  },
  {
    name: $localize`:@@period.selector.last-4-weeks:Last 4 weeks`,
    getValue: () => ({
      mode: 'relative',
      value: { method: 'last', value: 4, unit: 'weeks' },
    }),
  },
];

export const PERIOD_SELECTOR_MODES = [
  {
    name: $localize`:@@period.selector.modes.absolute:Exact`,
    value: 'absolute',
  },
  {
    name: $localize`:@@period.selector.modes.relative:Relative`,
    value: 'relative',
  },
  {
    name: $localize`:@@period.selector.modes.period:Period`,
    value: 'period',
  },
];

export const ABSOLUTE_METHODS = [
  {
    name: $localize`:@@period.selector.absolute.methods.at:Exactly at`,
    value: 'at',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.not-at:Not at`,
    value: 'not_at',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.before:Before`,
    value: 'before',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.after:After`,
    value: 'after',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.before-within:Before within`,
    value: 'before_within',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.after-within:After within`,
    value: 'after_within',
  },
];

export const STAT_ABSOLUTE_METHODS = [
  {
    name: $localize`:@@period.selector.absolute.methods.at:Exactly at`,
    value: 'at',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.before-within:Before within`,
    value: 'before_within',
  },
  {
    name: $localize`:@@period.selector.absolute.methods.after-within:After within`,
    value: 'after_within',
  },
];

export const PERIOD_METHODS = [
  {
    name: $localize`:@@period.selector.period.methods.between:In between`,
    value: 'between',
  },
  {
    name: $localize`:@@period.selector.period.methods.not-between:Not in between`,
    value: 'not_between',
  },
];

export const STAT_PERIOD_METHODS = [
  {
    name: $localize`:@@period.selector.period.methods.between:In between`,
    value: 'between',
  },
];

export const RELATIVE_METHODS = [
  {
    name: $localize`:@@period.selector.relative.methods.last:The last`,
    value: 'last',
  },
  {
    name: $localize`:@@period.selector.relative.methods.past:The past`,
    value: 'past',
  },
];

export const PERIOD_SELECTOR_DATE_UNITS = [
  {
    name: $localize`:@@period.selector.date.units.days:days`,
    value: 'days',
  },
  {
    name: $localize`:@@period.selector.date.units.weeks:weeks`,
    value: 'weeks',
  },
  {
    name: $localize`:@@period.selector.date.units.months:months`,
    value: 'months',
  },
  {
    name: $localize`:@@period.selector.date.units.years:years`,
    value: 'years',
  },
];

export const PERIOD_SELECTOR_DATE_TIME_UNITS = [
  {
    name: $localize`:@@period.selector.date.units.minutes:minutes`,
    value: 'minutes',
  },
  {
    name: $localize`:@@period.selector.date.units.hours:hours`,
    value: 'hours',
  },
  ...PERIOD_SELECTOR_DATE_UNITS,
];
